import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import StatusTimeline from "@/components/StatusTimeline";
import { api, API_BASE } from "@/lib/api";
import { STATUS_LABEL, STATUS_BADGE, PAYMENT_BADGE, STATUS_STEPS } from "@/lib/statusLabels";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Download, Trash2, MessageSquare, Save, Upload, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [statusVal, setStatusVal] = useState("");
  const [remarks, setRemarks] = useState("");
  const [newNote, setNewNote] = useState("");
  const [deliverables, setDeliverables] = useState([]);
  const [delivLabel, setDelivLabel] = useState("Deliverable");
  const [delivUploading, setDelivUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setData(data);
    setStatusVal(data.order.current_status);
    try {
      const r = await api.get(`/orders/${id}/deliverables`);
      setDeliverables(r.data.deliverables || []);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <DashboardLayout admin><div className="text-slate-500">Loading…</div></DashboardLayout>;

  const { order, documents, history, service, internal_notes } = data;

  const updateStatus = async () => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status: statusVal, remarks });
      toast.success("Status updated");
      setRemarks("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const confirmPayment = async () => {
    if (!window.confirm("Mark this order as PAID and email the invoice to the customer?")) return;
    try {
      const r = await api.post(`/admin/orders/${id}/confirm-payment`);
      toast.success(`Payment confirmed. Invoice ${r.data.invoice_number} emailed.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to confirm payment"); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/orders/${id}/notes`, { note: newNote });
      setNewNote("");
      toast.success("Note added");
      await load();
    } catch { toast.error("Failed to add note"); }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/admin/notes/${noteId}`);
      await load();
    } catch { toast.error("Failed"); }
  };

  const updateDocStatus = async (docId, status, reason = "") => {
    try {
      await api.put(`/admin/documents/${docId}/status`, { status, reason });
      toast.success("Document updated");
      await load();
    } catch { toast.error("Failed"); }
  };

  const downloadDoc = async (doc) => {
    const token = localStorage.getItem("w2e_token");
    const res = await fetch(`${API_BASE}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadDeliverable = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("File too large (max 15MB)");
    setDelivUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", delivLabel || "Deliverable");
    try {
      await api.post(`/admin/orders/${id}/deliverables`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Deliverable uploaded — customer notified");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setDelivUploading(false);
      e.target.value = "";
    }
  };

  const downloadDeliverable = async (dv) => {
    const token = localStorage.getItem("w2e_token");
    const res = await fetch(`${API_BASE}/deliverables/${dv.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = dv.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const deleteDeliverable = async (id2) => {
    if (!window.confirm("Delete this delivered file? The customer will lose access.")) return;
    try {
      await api.delete(`/admin/deliverables/${id2}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Failed"); }
  };

  return (
    <DashboardLayout admin>
      <Link to="/admin/orders" className="text-sm text-slate-500 hover:text-[#1E3A8A]">← All orders</Link>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <h1 className="font-display font-bold text-3xl text-[#0A192F] font-mono">{order.order_number || "Pending"}</h1>
        <span className={`status-badge ${STATUS_BADGE[order.current_status]}`}>{STATUS_LABEL[order.current_status]}</span>
        <span className={`status-badge ${PAYMENT_BADGE[order.payment_status]}`}>Payment: {order.payment_status}</span>
      </div>
      <div className="text-slate-600 mt-1">{order.service_name}</div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 mt-6">
        <div className="section-label mb-4">Document Status Tracker</div>
        <StatusTimeline current={order.current_status} />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Documents */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Documents</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-4">Customer uploads</div>
            {documents.length === 0 ? <div className="text-sm text-slate-500 py-4">No documents uploaded.</div> : (
              <ul className="divide-y divide-slate-100">
                {documents.map((d) => (
                  <li key={d.id} className="py-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#0A192F] truncate">{d.filename}</span>
                          <span className={`status-badge ${
                            d.status === "approved" ? "badge-success" :
                            d.status === "invalid" || d.status === "missing" ? "badge-error" : "badge-info"
                          }`}>{d.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {d.document_type} • {(d.size / 1024).toFixed(1)} KB • {new Date(d.uploaded_at).toLocaleString()}
                        </div>
                        {d.admin_reason && <div className="text-xs text-red-600 mt-0.5">Reason: {d.admin_reason}</div>}
                      </div>
                      <div className="flex gap-2 items-center">
                        <Button variant="outline" size="sm" onClick={() => downloadDoc(d)}><Download className="w-3.5 h-3.5" /></Button>
                        <Select value={d.status} onValueChange={(v) => {
                          if (v === "invalid" || v === "missing") {
                            const r = window.prompt("Reason (shown to customer):", d.admin_reason || "");
                            if (r == null) return;
                            updateDocStatus(d.id, v, r);
                          } else {
                            updateDocStatus(d.id, v, "");
                          }
                        }}>
                          <SelectTrigger className="w-32 h-9 text-xs" data-testid={`admin-doc-status-${d.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="received">Received</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="invalid">Invalid</SelectItem>
                            <SelectItem value="missing">Missing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Deliverables (admin → customer) */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-deliverables-panel">
            <div className="section-label">Deliverables</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Upload receipt / final documents for the customer</div>
            <div className="text-sm text-slate-500 mt-1">PDF / JPG / PNG up to 15MB. The customer will get an in-app notification and email.</div>

            <div className="mt-4 flex gap-2 items-center flex-wrap">
              <Input
                className="max-w-xs"
                value={delivLabel}
                onChange={(e) => setDelivLabel(e.target.value)}
                placeholder="Label (e.g. GST Certificate)"
                data-testid="deliverable-label-input"
              />
              <label className="inline-flex">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadDeliverable} className="hidden" disabled={delivUploading} data-testid="deliverable-upload-input" id={`del-file-${id}`} />
                <Button
                  type="button"
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF]"
                  disabled={delivUploading}
                  onClick={() => document.getElementById(`del-file-${id}`).click()}
                  data-testid="deliverable-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> {delivUploading ? "Uploading…" : "Upload deliverable"}
                </Button>
              </label>
            </div>

            {deliverables.length > 0 && (
              <ul className="divide-y divide-slate-100 mt-4">
                {deliverables.map((dv) => (
                  <li key={dv.id} className="py-3 flex items-center justify-between gap-3" data-testid={`deliverable-row-${dv.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileCheck2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="font-medium text-[#0A192F] truncate">{dv.label}</span>
                        <span className="text-xs text-slate-500">· {dv.filename}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {(dv.size / 1024).toFixed(1)} KB • uploaded by {dv.uploaded_by_name} • {new Date(dv.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadDeliverable(dv)} data-testid={`deliverable-dl-${dv.id}`}><Download className="w-3.5 h-3.5" /></Button>
                      <Button variant="outline" size="sm" onClick={() => deleteDeliverable(dv.id)} data-testid={`deliverable-rm-${dv.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Status management */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-status-panel">            <div className="section-label">Status Management</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-4">Update order status</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Select value={statusVal} onValueChange={setStatusVal}>
                <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_STEPS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Optional remarks (visible to customer)" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="admin-status-remarks" />
            </div>
            <Button className="mt-3 bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={updateStatus} data-testid="admin-status-save-btn">
              <Save className="w-4 h-4 mr-1" /> Save status
            </Button>
          </div>

          {/* Internal notes */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-notes-panel">
            <div className="section-label">Internal Notes</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Admin-only — not visible to customer</div>
            <form onSubmit={addNote} className="mt-4 flex gap-2 items-start">
              <Textarea rows={2} placeholder="Add a private note…" value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="admin-note-input" />
              <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="admin-note-add-btn"><MessageSquare className="w-4 h-4 mr-1" /> Add</Button>
            </form>
            <ul className="mt-4 space-y-3">
              {internal_notes?.length === 0 && <li className="text-sm text-slate-500">No internal notes yet.</li>}
              {internal_notes?.map((n) => (
                <li key={n.id} className="p-3 rounded-md border border-slate-200 bg-slate-50">
                  <div className="text-sm text-[#0A192F] whitespace-pre-line">{n.note}</div>
                  <div className="mt-2 flex justify-between items-center text-xs text-slate-500">
                    <span>{n.admin_name} • {new Date(n.created_at).toLocaleString()}</span>
                    <Button variant="ghost" size="sm" onClick={() => deleteNote(n.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* History */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Status history</div>
            <ol className="mt-4 space-y-3">
              {history.slice().reverse().map((h, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-2 h-2 mt-1.5 rounded-full bg-[#1E3A8A]" />
                  <div>
                    <div className="text-sm font-medium">{STATUS_LABEL[h.to_status] || h.to_status}</div>
                    <div className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString()} · {h.changed_by_name || "System"}</div>
                    {h.remarks && <div className="text-xs text-slate-600 mt-0.5">{h.remarks}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Customer</div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="font-medium text-[#0A192F]">{order.full_name}</div>
              <div className="text-slate-600">{order.email}</div>
              <div className="text-slate-600">{order.mobile}</div>
              {order.pan && <div className="font-mono text-xs text-slate-600">PAN: {order.pan}</div>}
              {order.address && <div className="text-slate-600 text-xs mt-2">{order.address}</div>}
              {order.additional_info && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Additional info</div>
                  <div className="text-xs text-slate-700 whitespace-pre-line">{order.additional_info}</div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Order & payment</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">Service</span><span className="text-right">{order.service_name}{order.plan_name ? <><br/><span className="text-xs text-slate-600">{order.plan_name}{order.amount ? ` (₹${Number(order.amount).toLocaleString("en-IN")})` : ""}</span></> : null}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Date</span><span>{new Date(order.created_at).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-medium">₹{order.amount}</span></div>
              {order.cashfree_order_id && (
                <div className="flex justify-between"><span className="text-slate-500">CF Order</span><span className="font-mono text-xs truncate max-w-[60%]">{order.cashfree_order_id}</span></div>
              )}
              {order.cashfree_payment_id && (
                <div className="flex justify-between"><span className="text-slate-500">Payment ID</span><span className="font-mono text-xs truncate max-w-[60%]">{order.cashfree_payment_id}</span></div>
              )}
              {order.invoice_number && (
                <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span className="font-mono text-xs">{order.invoice_number}</span></div>
              )}
            </div>
            {order.payment_status !== "paid" && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-2">Received payment offline? Mark this order as paid and email the invoice.</div>
                <Button
                  onClick={confirmPayment}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="admin-confirm-payment-btn"
                >
                  <FileCheck2 className="w-4 h-4 mr-1" /> Confirm Payment & Send Invoice
                </Button>
              </div>
            )}
            {order.payment_status === "paid" && order.payment_mode === "manual" && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Button
                  onClick={confirmPayment}
                  variant="outline"
                  className="w-full"
                  data-testid="admin-resend-invoice-btn"
                >
                  <FileCheck2 className="w-4 h-4 mr-1" /> Resend invoice email
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
